import { useEffect, useRef, useState } from 'react';
import { X, RotateCcw, ZoomIn, ZoomOut } from 'lucide-react';
import { track } from '@/lib/track';

// Shared full-screen viewer for the stitched brochure spreads.
// Pinch (Android/iOS) + mouse-wheel zoom, drag to inspect, reset, close,
// Escape support, iOS safe-area padding, background scroll lock + restore.
export default function FullscreenViewer({ src, alt, onClose }) {
  const [t, setT] = useState({ scale: 1, x: 0, y: 0 });
  const pointers = useRef(new Map());
  const pinch = useRef(null);
  const last = useRef(null);

  const clamp = (s) => Math.min(5, Math.max(1, s));
  const apply = (scale, x, y) => setT(scale <= 1 ? { scale: 1, x: 0, y: 0 } : { scale, x, y });
  const zoomBy = (d) => apply(clamp(t.scale + d), t.x, t.y);
  const reset = () => apply(1, 0, 0);

  // lock background scroll while open; restore the exact scroll position on close
  useEffect(() => {
    const y = window.scrollY;
    const lenis = window.__lenis;
    document.body.style.overflow = 'hidden';
    if (lenis) lenis.stop();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    track('image_fullscreen_open', { src });
    return () => {
      document.body.style.overflow = '';
      if (lenis) { lenis.start(); lenis.scrollTo(y, { immediate: true }); }
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose, src]);

  const onWheel = (e) => {
    e.preventDefault();
    zoomBy(e.deltaY < 0 ? 0.3 : -0.3);
  };

  const onPointerDown = (e) => {
    e.target.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale: t.scale };
    }
    last.current = { x: e.clientX, y: e.clientY };
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2 && pinch.current) {
      const [a, b] = [...pointers.current.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      apply(clamp(pinch.current.scale * (d / pinch.current.dist)), t.x, t.y);
      return;
    }
    if (t.scale > 1 && last.current) {
      apply(t.scale, t.x + (e.clientX - last.current.x), t.y + (e.clientY - last.current.y));
      last.current = { x: e.clientX, y: e.clientY };
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (t.scale <= 1.02) reset();
  };

  return (
    <div
      data-testid="fullscreen-viewer"
      data-lenis-prevent
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      className="fixed inset-0 z-[90] bg-[#14151A]/96 backdrop-blur-md"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
    >
      <div className="absolute right-4 top-[calc(1rem+env(safe-area-inset-top))] z-10 flex gap-2">
        <button data-testid="viewer-zoom-out" aria-label="Zoom out" onClick={() => zoomBy(-0.5)} className="flex h-11 w-11 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white backdrop-blur-md transition-colors hover:bg-white/20">
          <ZoomOut size={17} />
        </button>
        <button data-testid="viewer-zoom-in" aria-label="Zoom in" onClick={() => zoomBy(0.5)} className="flex h-11 w-11 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white backdrop-blur-md transition-colors hover:bg-white/20">
          <ZoomIn size={17} />
        </button>
        <button data-testid="viewer-reset" aria-label="Reset zoom" onClick={reset} className="flex h-11 w-11 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white backdrop-blur-md transition-colors hover:bg-white/20">
          <RotateCcw size={17} />
        </button>
        <button data-testid="viewer-close" aria-label="Close full screen" onClick={onClose} className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#1C1D21] shadow-lg transition-transform hover:scale-105 active:scale-95">
          <X size={18} />
        </button>
      </div>

      <div
        className="flex h-full w-full touch-none items-center justify-center overflow-hidden"
        style={{ touchAction: 'none' }}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={() => (t.scale > 1 ? reset() : apply(2.2, 0, 0))}
      >
        <img
          src={src}
          alt={alt}
          draggable="false"
          className="max-h-full max-w-full select-none object-contain"
          style={{ transform: `translate(${t.x}px, ${t.y}px) scale(${t.scale})`, transition: 'transform 75ms linear' }}
        />
      </div>

      {t.scale > 1 && (
        <span data-testid="viewer-zoom-badge" className="absolute bottom-[calc(1rem+env(safe-area-inset-bottom))] left-4 rounded-full bg-white/12 px-3 py-1 text-[11px] font-semibold text-white backdrop-blur-md">
          {Math.round(t.scale * 100)}%
        </span>
      )}
    </div>
  );
}
